import React, { useState, useEffect, useRef, useMemo } from 'react'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { Avatar, makeStyles, TextField, Typography } from '@material-ui/core'
import { debounce } from 'lodash'
import useHouseholds from '../../hooks/useHouseholds'
import useSelectedFacility from '../../hooks/useSelectedFacility'
import PetBlock from './components/PetBlock'

const useStyles = makeStyles(() => ({
	householdSearch: {
		'& .MuiAutocomplete-inputRoot': {
			paddingTop: '8px'
		}
	}
}))

const PetAutocomplete = ({ onHouseholdSelected, additionalSearchParams = {}, pet = {}, togglePet, selectedPets = [] }) => {
	const classes = useStyles()

	const { facility } = useSelectedFacility()
	const { households, getHouseholdsByMarketId, getHouseholdsWithAdvancedSearch, clearHouseholds } =
		useHouseholds()
	const [household, setHousehold] = useState(null)
    const [loading, setLoading] = useState(false)
	const [open, setOpen] = React.useState(false)
	const [search, setSearch] = useState('')
    const [results, setResults] = useState([])

	const delayedSetSearch = useRef(debounce((t) => setSearch(t), 750)).current

	const updateSearch = (value) => delayedSetSearch(value)

	useEffect(() => {
		if (search && facility) {
            async function doSearch() {
                setLoading(true)
                const isPetSearch = true
                const searchResults = await getHouseholdsWithAdvancedSearch({
                    marketId: facility.market_id,
                    search: encodeURIComponent(search),
                    searchBy: 'contact',
                    isPetSearch,
                    ...additionalSearchParams
                })

                if (searchResults?.length) setResults(searchResults)
                setLoading(false)
            }
           
            doSearch()
		} else {
			clearHouseholds()
		}
	}, [search, facility, additionalSearchParams])

	useEffect(() => {
		if (!open) {
			clearHouseholds()
			setSearch('')
		}
	}, [open])

    const mappedHouses = useMemo(() => {
        const houses = []

        results.forEach((result) => {
            if (result?.pets?.length) {
                let primary = result.accounts.find(a => !!a.primary)
                if (!primary) primary = result.accounts[0]
                // result.pets.forEach((pet) => {
                //     pets.push({ ...pet, full_name: `${pet.name} ${primary.last_name}`})
                // })

                if (primary) houses.push({ ...result, full_name: `${primary.first_name} ${primary.last_name}`, last_name: primary.last_name })
            }
        })

        return houses

    }, [results])


	const handleHouseholdSelected = (household) => {
		setHousehold(household)
		if (onHouseholdSelected) {
			onHouseholdSelected(household)
		}
	}

	const clearHousehold = () => {
		handleHouseholdSelected(null)
	}

	return !household ? (
		<Autocomplete
			id="client-select"
			getOptionLabel={(option) =>
				typeof option === 'string' ? option?.full_name : option?.full_name || 'No Name'
			}
			filterOptions={(x) => x}
			options={mappedHouses}
			className={classes.householdSearch}
			autoComplete
			noOptionsText="No other contacts"
			loading={loading}
			includeInputInList
			disableOpenOnFocus
			open={open}
			onOpen={() => {
				setOpen(true)
			}}
			onClose={() => {
				setOpen(false)
			}}
			onChange={(event, newValue) => {
				handleHouseholdSelected(newValue)
			}}
			renderOption={(option) => (
				<React.Fragment>
					<div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center' }}>
						<Typography variant="body2">{option?.full_name}</Typography>
					</div>
				</React.Fragment>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Search existing clients"
					variant="filled"
					fullWidth
					onChange={(e) => updateSearch(e.target.value)}
				/>
			)}
		/>
	) : (
		<PetBlock 
            household={household} 
            onClearHousehold={clearHousehold} 
            pet={pet} 
            togglePet={togglePet} 
            selectedPets={selectedPets}/>
	)
}

export default PetAutocomplete
